import React, { FC, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Grid, GridItem } from '../../shared/Grid';
import { GamesCarouselGrid } from './GamesCarouselGrid';
import { LobbiesData } from '../games.model';
import { GridWidth, TileRowCollection } from '../GameCard/GameCard.types';
import { joinStrings } from '../../../utils/string';
import { selectSelectedCategory } from '../../../state/app/selectors';

type LobbyItem = NonNullable<LobbiesData['lobbyItems']>[number];

type LobbyGroupType = 'full' | 'half-pair' | 'half-single';

interface LobbyItemMeta {
  item: LobbyItem;
  tileCollections: TileRowCollection[];
  viewAllTarget: string;
  hasHalfWidth: boolean;
}

interface LobbyGroup {
  type: LobbyGroupType;
  items: LobbyItemMeta[];
}

interface CarouselConfig {
  showArrows?: boolean;
  showDots?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  fallbackImageUrl?: string;
  fallbackImageAltText?: string;
}

interface GroupedGamesCarouselGridProps {
  lobbyItems?: LobbiesData['lobbyItems'];
  buildTileRowCollections: (items: LobbiesData['lobbyItems']) => TileRowCollection[];
  disabled: boolean;
  carouselConfig?: CarouselConfig;
  category?: string;
}

const getLobbyItemKey = (item: LobbyItem, fallbackIndex: number): string =>
  item.internalName || item.slug || item.title || `lobby-item-${fallbackIndex}`;

const groupLobbyItems = (itemMetas: LobbyItemMeta[]): LobbyGroup[] => {
  const groups: LobbyGroup[] = [];

  let index = 0;

  while (index < itemMetas.length) {
    const meta = itemMetas[index];

    if (meta.hasHalfWidth) {
      const nextMeta = itemMetas[index + 1];

      if (nextMeta && nextMeta.hasHalfWidth) {
        groups.push({
          type: 'half-pair',
          items: [meta, nextMeta],
        });
        index += 2;
        continue;
      }

      groups.push({
        type: 'half-single',
        items: [meta],
      });
      index += 1;
      continue;
    }

    groups.push({
      type: 'full',
      items: [meta],
    });
    index += 1;
  }

  return groups;
};

export const GroupedGamesCarouselGrid: FC<GroupedGamesCarouselGridProps> = ({
  lobbyItems,
  buildTileRowCollections,
  disabled,
  carouselConfig,
  category,
}) => {
  const {
    showArrows = false,
    showDots = true,
    autoplay = false,
    autoplayInterval = 5000,
    fallbackImageUrl = '',
    fallbackImageAltText = '',
  } = carouselConfig || {};

  const items: LobbyItem[] = lobbyItems ?? [];
  const selectedCategory = useSelector(selectSelectedCategory);

  const itemMetas: LobbyItemMeta[] = useMemo(() => {
    return items.map((item) => {
      const tileCollections = buildTileRowCollections([item]);
      const viewAllTarget = item.title || tileCollections[0]?.internal_name || '';
      const hasHalfWidth = tileCollections.some((collection) => collection.Width === GridWidth.HALF);

      return {
        item,
        tileCollections,
        viewAllTarget,
        hasHalfWidth,
      };
    });
  }, [items, buildTileRowCollections]);

  const groupedLobbyItems = useMemo(() => groupLobbyItems(itemMetas), [itemMetas]);

  if (groupedLobbyItems.length === 0) {
    return null;
  }

  const renderCarousel = (meta: LobbyItemMeta): ReactNode => (
    <GamesCarouselGrid
      tileRowCollections={meta.tileCollections}
      games={meta.item.games || []}
      title={meta.item.title}
      disabled={disabled}
      showArrows={showArrows}
      showDots={showDots}
      autoplay={autoplay}
      autoplayInterval={autoplayInterval}
      fallbackImageUrl={fallbackImageUrl}
      fallbackImageAltText={fallbackImageAltText}
      categorySlug={meta.item.slug}
      categoryInternalName={meta.item.internalName}
      category={category}
    />
  );

  return (
    <>
      {groupedLobbyItems.map((group, groupIndex) => {
        if (group.type === 'full') {
          const [meta] = group.items;
          const itemKey = getLobbyItemKey(meta.item, groupIndex);

          return <div key={itemKey}>{renderCarousel(meta)}</div>;
        }

        if (group.type === 'half-pair') {
          const isGroupExpanded = group.items.some(
            (meta) => meta.viewAllTarget && meta.viewAllTarget === selectedCategory
          );

          return (
            <Grid
              columns={2}
              className={joinStrings([
                'half-width-carousel-row',
                isGroupExpanded && 'half-width-carousel-row--grid-expanded',
              ])}
              key={`group-${groupIndex}`}
            >
              {group.items.map((meta, itemIndex) => {
                const itemKey = getLobbyItemKey(meta.item, groupIndex * 10 + itemIndex);

                return <GridItem key={itemKey}>{renderCarousel(meta)}</GridItem>;
              })}
            </Grid>
          );
        }

        const [meta] = group.items;
        const itemKey = getLobbyItemKey(meta.item, groupIndex);
        const isExpanded = meta.viewAllTarget && meta.viewAllTarget === selectedCategory;

        return (
          <div
            className={joinStrings([
              'half-width-carousel-single',
              isExpanded && 'half-width-carousel-single--grid-expanded',
            ])}
            key={itemKey}
          >
            {renderCarousel(meta)}
          </div>
        );
      })}
    </>
  );
};
